// Data des principales villes françaises pour pages programmatic SEO
// Chaque ville a contenu unique : stats économiques, secteurs dominants, exemples chiffrés.
// → Évite le duplicate content (pénalité Google) tout en générant 15+ pages SEO locales.

export const CITIES = [
  {
    slug: 'paris',
    name: 'Paris',
    nameLong: 'Paris (75)',
    region: 'Île-de-France',
    population: '2,1 M habitants · 13 M en Île-de-France',
    pmeCount: '600 000+ TPE/PME',
    economy: "Première place économique de France et d'Europe. Capitale de la finance, du tourisme, du luxe, de la tech et des services aux entreprises. La densité d'expertises et de capital crée un terrain ultra-favorable aux cessions/acquisitions.",
    topSectors: [
      { name: 'Tech / SaaS', multiple: '8-14x EBITDA', note: 'Écosystème Station F, plus de 1 000 startups, ETI tech matures (Mirakl, Doctolib, Datadog, Algolia, Aircall)' },
      { name: 'Services B2B / Conseil', multiple: '5-8x EBITDA', note: 'Forte concentration de cabinets de conseil, audit, communication, marketing. Multiples élevés grâce à la récurrence et la sophistication clients' },
      { name: 'Luxe & Mode', multiple: '6-12x EBITDA', note: 'Capitale mondiale du luxe — multiples premium pour marques avec notoriété' },
      { name: 'Finance / Fintech', multiple: '10-18x EBITDA', note: 'Hub fintech européen (Lydia, Qonto, Alma, Younited). Valorisations IA + récurrence très élevées' },
    ],
    example: {
      title: "Exemple : Cabinet de conseil parisien — 600K€ CA",
      desc: "Cabinet conseil RH avec CA 600K€, EBITDA 18% (108K€), 80% de récurrence et 40% de clients CAC 40. Multiple appliqué : 6,5x EBITDA = valorisation 700K€ (vs 4,5x médiane nationale du secteur). La prime géographique parisienne se justifie par la qualité client et la liquidité du marché M&A.",
    },
    localContext: "Paris bénéficie d'un écosystème M&A très liquide : 40% des cessions PME françaises s'y déroulent. Les valorisations y sont 15 à 25% supérieures à la moyenne nationale pour les secteurs intellectuels (conseil, tech, finance, communication). Côté fiscalité, le Pacte Dutreil permet 75% d'abattement sur la transmission familiale.",
    specificities: [
      "Marché M&A profond → cessions rapides (3-6 mois vs 8-12 en province)",
      "Multiples premium pour la tech (Station F, La French Tech)",
      "Forte demande étrangère pour entreprises parisiennes (anglo-saxonne, asiatique)",
      "Coûts immobiliers à intégrer dans la valorisation (loyers commerciaux)",
    ],
  },
  {
    slug: 'lyon',
    name: 'Lyon',
    nameLong: 'Lyon (69)',
    region: 'Auvergne-Rhône-Alpes',
    population: '520 000 habitants · 2,3 M dans la métropole',
    pmeCount: '180 000+ TPE/PME',
    economy: "Deuxième métropole économique française. Pôles d'excellence en santé/biotechs (cluster Lyonbiopôle), chimie (Vallée de la Chimie), banque/assurance (Crédit Agricole, AXA), agroalimentaire et tech. Tradition industrielle forte.",
    topSectors: [
      { name: 'Santé / Biotech', multiple: '6-10x EBITDA', note: 'Cluster Lyonbiopôle (Sanofi Pasteur, bioMérieux, Boehringer Ingelheim). Multiples élevés pour PME bio/medtech' },
      { name: 'Industrie / Manufacture', multiple: '4-7x EBITDA', note: 'Tissu industriel dense, sous-traitance auto/aéro/pharma. Multiples solides si carnet de commandes diversifié' },
      { name: 'Tech / Numérique', multiple: '7-12x EBITDA', note: 'Lyon French Tech : EM Lyon, INSA, écosystème dev mature' },
      { name: 'Agroalimentaire', multiple: '4-7x EBITDA', note: 'Capitale gastronomique, halles de Lyon, MOF, restauration. PME agro avec marques fortes' },
    ],
    example: {
      title: "Exemple : PME industrielle lyonnaise — 1,2M€ CA",
      desc: "Mécanique de précision, CA 1,2M€, EBITDA 14% (168K€), clients aéronautique (40%) + automobile (35%) + médical (25%). Multiple appliqué : 5,5x EBITDA = valorisation 925K€. La diversification clients et l'exposition aéro/médical justifient un multiple supérieur aux 4,5x médiane industrie.",
    },
    localContext: "Lyon offre un écosystème équilibré entre industrie traditionnelle et nouvelles technologies. Les PME locales bénéficient d'un fort soutien régional (Région Auvergne-Rhône-Alpes, Bpifrance Lyon). Les transmissions familiales sont nombreuses (tissu de PMI multi-générations), favorisant le recours au Pacte Dutreil pour optimiser la fiscalité.",
    specificities: [
      "Forte densité de cabinets M&A régionaux (CIC Conseil, In Extenso Finance, BDO)",
      "Multiples industrie 10-15% au-dessus de la moyenne nationale (bassin technique)",
      "Excellence biotech/medtech → primes valorisation",
      "Marché immobilier d'entreprise dynamique (loyers raisonnables vs Paris)",
    ],
  },
  {
    slug: 'marseille',
    name: 'Marseille',
    nameLong: 'Marseille (13)',
    region: "Provence-Alpes-Côte d'Azur",
    population: '870 000 habitants · 1,9 M dans la métropole Aix-Marseille',
    pmeCount: '160 000+ TPE/PME',
    economy: "Premier port de France et deuxième pôle économique méditerranéen. Forte présence du maritime, de la logistique, du tourisme, des industries pétrochimiques (Fos-sur-Mer) et de la santé. Émergence rapide d'un écosystème tech (French Tech Aix-Marseille).",
    topSectors: [
      { name: 'Maritime & Logistique', multiple: '4-7x EBITDA', note: 'Port autonome (CMA CGM, transit, freight forwarding). Multiples valorisés si contrats long terme' },
      { name: 'Tourisme / Hôtellerie', multiple: '4-8x EBITDA', note: 'Calanques, MUCEM, 8 M de touristes/an. PME hôtelières/restauration avec saisonnalité maîtrisée' },
      { name: 'Santé & Médical', multiple: '5-9x EBITDA', note: 'AP-HM, biotech, télémédecine. Cluster Eurobiomed' },
      { name: 'Numérique / French Tech', multiple: '7-12x EBITDA', note: 'The Camp, Aix Marseille French Tech, plus de 300 startups' },
    ],
    example: {
      title: "Exemple : Société de transit portuaire — 2M€ CA",
      desc: "Commissionnaire de transport spécialisé conteneurs, CA 2M€, EBITDA 12% (240K€), 60% de contrats récurrents avec armateurs. Multiple appliqué : 5,5x EBITDA = valorisation 1,3M€. La position stratégique sur le port de Marseille-Fos et la récurrence justifient un multiple supérieur à 4x médiane logistique.",
    },
    localContext: "L'économie marseillaise se diversifie rapidement : au-delà du maritime historique, la métropole attire startups tech et entreprises de santé. Les valorisations PME y sont alignées sur la moyenne nationale, avec des primes pour les acteurs ayant un ancrage portuaire ou méditerranéen unique.",
    specificities: [
      "Multiples portuaires/logistique premium si lien avec CMA CGM",
      "Marché hôtelier sensible à la saisonnalité — valorisation EBITDA normalisée",
      "Écosystème tech jeune mais en croissance rapide",
      "Bassin de repreneurs régionaux + investisseurs italiens/espagnols",
    ],
  },
  {
    slug: 'toulouse',
    name: 'Toulouse',
    nameLong: 'Toulouse (31)',
    region: 'Occitanie',
    population: '500 000 habitants · 1,5 M dans la métropole',
    pmeCount: '120 000+ TPE/PME',
    economy: "Capitale européenne de l'aéronautique et du spatial (Airbus, Thales Alenia Space, ATR). Pôle de compétitivité mondial Aerospace Valley. Forte concentration d'ingénieurs et de PME tech, biotechs et numériques.",
    topSectors: [
      { name: 'Aéronautique & Spatial', multiple: '5-9x EBITDA', note: 'Sous-traitance Airbus, ATR, Thales. Multiples élevés si qualification A1/A2 et carnet de commandes 2-3 ans' },
      { name: 'Tech / SaaS', multiple: '7-13x EBITDA', note: 'Toulouse French Tech, plus de 500 startups, écoles d\'ingénieurs (INSA, ENAC, ISAE-Supaéro)' },
      { name: 'Santé & Biotech', multiple: '6-10x EBITDA', note: 'Cancéropôle, Oncopole, Pierre Fabre. Multiples premium pour biotechs avec brevets' },
      { name: 'Agroalimentaire', multiple: '4-7x EBITDA', note: 'Sud-Ouest agricole : vins, foie gras, fromages, viandes. Marques régionales bien valorisées' },
    ],
    example: {
      title: "Exemple : Sous-traitant aéro toulousain — 800K€ CA",
      desc: "Mécanique de précision pièces avion, CA 800K€, EBITDA 16% (128K€), qualification EN9100 + AS9100, principal client Airbus (60%) + Latécoère (25%). Multiple appliqué : 6x EBITDA = valorisation 770K€. La qualification aéro et la stabilité Airbus justifient un multiple supérieur à 4,5x médiane industrie.",
    },
    localContext: "Toulouse bénéficie d'un écosystème aéronautique unique en Europe, avec valorisations premium pour PME sous-traitantes qualifiées. L'arrivée de plus en plus d'ETI tech (Sigfox, Hubster, Sopra) tire les multiples du numérique vers le haut. Bpifrance Toulouse très active sur les transmissions.",
    specificities: [
      "Multiples aéro 20-30% supérieurs à la moyenne (rareté qualifications EN9100)",
      "Forte demande Airbus + Tier 1 (Latécoère, Daher, Stelia) pour reprises ciblées",
      "Cluster spatial NewSpace en croissance (Hemeria, Loft Orbital)",
      "Bassin de cadres techniques → reprise interne fréquente",
    ],
  },
  {
    slug: 'bordeaux',
    name: 'Bordeaux',
    nameLong: 'Bordeaux (33)',
    region: 'Nouvelle-Aquitaine',
    population: '260 000 habitants · 820 000 dans la métropole',
    pmeCount: '90 000+ TPE/PME',
    economy: "Capitale mondiale du vin, premier vignoble français en valeur. Pôle aéronautique (Dassault, Thales), tech en forte croissance (French Tech Bordeaux), santé. Économie diversifiée avec ancrage agroalimentaire premium.",
    topSectors: [
      { name: 'Vins & Spiritueux', multiple: '5-10x EBITDA + valeur foncière', note: 'Châteaux, négociants, courtiers. Valorisation atypique mêlant DCF + actif foncier (vignobles)' },
      { name: 'Aéronautique', multiple: '5-9x EBITDA', note: 'Dassault, Thales, Stelia. Multiples solides pour sous-traitants Tier 2/3' },
      { name: 'Tech / SaaS', multiple: '7-12x EBITDA', note: 'Bordeaux French Tech : Cdiscount, Yousign, Sunday. Cluster dynamique' },
      { name: 'Tourisme / Œnotourisme', multiple: '4-7x EBITDA', note: 'Cité du Vin, châteaux ouverts au public, restauration gastronomique' },
    ],
    example: {
      title: "Exemple : Négoce de vins bordelais — 3M€ CA",
      desc: "Négociant vins de Bordeaux, CA 3M€, EBITDA 8% (240K€), 50% export (UK, Asie), 40% restaurateurs France. Multiple appliqué : 6x EBITDA = 1,4M€. Valorisation totale 1,9M€ après ajout du stock vieillissant (valeur de marché) et de la marque (notoriété historique).",
    },
    localContext: "Bordeaux présente une particularité : pour les PME du vin, la valorisation classique (DCF, multiples EBITDA) doit être combinée à l'évaluation du foncier viticole et des stocks. Une parcelle en AOC Saint-Émilion Grand Cru vaut 1-2M€/hectare. Les transmissions familiales utilisent massivement le Pacte Dutreil (75% d'abattement) et les groupements fonciers viticoles (GFV).",
    specificities: [
      "Valorisation viticole = EBITDA + foncier + stocks + marque (méthode mixte)",
      "Demande étrangère forte (Chinois, Américains, Britanniques) pour châteaux",
      "Transmissions familiales optimisées (Pacte Dutreil systématique)",
      "Émergence rapide tech / fintech depuis 2020",
    ],
  },
  {
    slug: 'lille',
    name: 'Lille',
    nameLong: 'Lille (59)',
    region: 'Hauts-de-France',
    population: '235 000 habitants · 1,2 M dans la métropole européenne',
    pmeCount: '110 000+ TPE/PME',
    economy: "Plateforme logistique stratégique entre Paris, Bruxelles et Londres. Tissu de PME retail/e-commerce dense (Auchan, Decathlon, Leroy Merlin, Boulanger, Kiabi). Pôle textile historique, tech en croissance (EuraTechnologies).",
    topSectors: [
      { name: 'Retail & E-commerce', multiple: '4-7x EBITDA', note: 'Bassin historique des grandes enseignes Mulliez. Multiples solides pour PME avec marque' },
      { name: 'Logistique & Transport', multiple: '4-6x EBITDA', note: 'Position Paris-Bruxelles-Londres. Multiples valorisés si contrats long terme grands comptes' },
      { name: 'Tech / Numérique', multiple: '7-12x EBITDA', note: 'EuraTechnologies (plus grand incubateur français), Vade Secure, OVHcloud (proximité)' },
      { name: 'Textile / Mode', multiple: '3-6x EBITDA', note: 'Roubaix-Tourcoing : héritage textile, marques émergentes (Sézane, Marie Sixtine)' },
    ],
    example: {
      title: "Exemple : E-commerce lillois — 1,5M€ CA",
      desc: "Pure player e-commerce mode féminine, CA 1,5M€, EBITDA 13% (195K€), 70K visiteurs/mois, marque propriétaire, 30% de récurrence client. Multiple appliqué : 6x EBITDA = valorisation 1,17M€. La marque + l'audience récurrente justifient un multiple supérieur à 4,5x médiane retail.",
    },
    localContext: "Lille offre un écosystème unique pour le retail/e-commerce, avec une concentration exceptionnelle d'enseignes (galaxie Mulliez : Auchan, Decathlon, Leroy Merlin, Boulanger, Kiabi, Norauto). Les PME locales bénéficient de la proximité de ces majors pour partenariats ou exits stratégiques.",
    specificities: [
      "Multiples retail/e-commerce 10-15% au-dessus de la moyenne (bassin spécialisé)",
      "Exits stratégiques fréquents vers grandes enseignes Mulliez",
      "Logistique avantagée par position frontalière (Belgique, UK)",
      "EuraTechnologies = vivier de scale-ups tech",
    ],
  },
  {
    slug: 'nantes',
    name: 'Nantes',
    nameLong: 'Nantes (44)',
    region: 'Pays de la Loire',
    population: '320 000 habitants · 1 M dans la métropole',
    pmeCount: '100 000+ TPE/PME',
    economy: "Métropole en très forte croissance démographique et économique. Tissu industriel solide (aéronautique, navale, agro), French Tech dynamique, économie créative. Bassin de PME ETI familiales avec belles transmissions.",
    topSectors: [
      { name: 'Aéronautique & Navale', multiple: '5-8x EBITDA', note: 'Airbus, Stelia, STX (Chantiers de Saint-Nazaire). Multiples valorisés si qualifications' },
      { name: 'Tech / Numérique', multiple: '7-12x EBITDA', note: 'Nantes French Tech : Akeneo, Manomano, iAdvize. Bassin développeurs important' },
      { name: 'Agroalimentaire', multiple: '4-7x EBITDA', note: 'Pôle Valorial, biscuiteries (LU/Mondelez), distribution (Système U)' },
      { name: 'Économie créative', multiple: '4-8x EBITDA', note: 'Île de Nantes : design, architecture, communication, audiovisuel' },
    ],
    example: {
      title: "Exemple : SaaS nantais B2B — 800K€ ARR",
      desc: "Logiciel SaaS RH, ARR 800K€, croissance +35%/an, churn 6% net negative (expansion clients), 80 clients PME. Multiple appliqué : 8x ARR = valorisation 6,4M€. La croissance élevée et le NRR > 100% justifient un multiple supérieur à 6x médiane SaaS.",
    },
    localContext: "Nantes attire de plus en plus d'entrepreneurs tech et de PME en quête de qualité de vie. Le tissu industriel historique (aéro Saint-Nazaire, navale) cohabite avec une scène startup très active. Les transmissions familiales sont nombreuses dans l'agro et l'industrie classique.",
    specificities: [
      "Forte attractivité talents tech → primes valorisation pour PME avec équipe stable",
      "Pôle naval Saint-Nazaire unique en France (paquebots, éolien offshore)",
      "Multiples SaaS dynamiques (cluster mature)",
      "Bpifrance Pays de la Loire actif sur transmissions",
    ],
  },
  {
    slug: 'strasbourg',
    name: 'Strasbourg',
    nameLong: 'Strasbourg (67)',
    region: 'Grand Est',
    population: '290 000 habitants · 510 000 dans l\'Eurométropole',
    pmeCount: '70 000+ TPE/PME',
    economy: "Capitale européenne (Parlement, Conseil de l'Europe), position frontalière avec l'Allemagne. Tissu industriel solide (pharmacie, automobile, mécanique), pôle bancaire/assurance, économie transfrontalière dynamique.",
    topSectors: [
      { name: 'Pharmacie / Santé', multiple: '6-10x EBITDA', note: 'Cluster Alsace BioValley, Lilly, Eli Lilly, Bischwiller. Multiples premium pour PME avec brevets' },
      { name: 'Automobile / Mécanique', multiple: '4-7x EBITDA', note: 'Sous-traitance Mercedes Hambach, Smart, Stellantis. Multiples industrie classiques' },
      { name: 'Banque / Assurance', multiple: '5-9x EBITDA', note: 'Crédit Mutuel (siège), Caisse Épargne, MAAF. Bassin services financiers' },
      { name: 'Économie transfrontalière', multiple: '4-7x EBITDA', note: 'PME exportant en Allemagne — multiples premium si récurrence Grands Comptes DE' },
    ],
    example: {
      title: "Exemple : PME mécanique alsacienne — 1,8M€ CA",
      desc: "Usinage de précision, CA 1,8M€, EBITDA 12% (216K€), 50% clients allemands (Bosch, Siemens). Multiple appliqué : 5,5x EBITDA = valorisation 1,2M€. L'exposition Allemagne et la qualité technique justifient un multiple supérieur à 4,5x médiane industrie.",
    },
    localContext: "Strasbourg bénéficie d'un positionnement transfrontalier unique : 40% des PME alsaciennes exportent en Allemagne. Cette internationalisation, combinée à un tissu pharmaceutique solide (BioValley), tire les valorisations vers le haut. Les transmissions familiales sont nombreuses (tradition entrepreneuriale alsacienne).",
    specificities: [
      "Primes valorisation pour PME avec clientèle allemande récurrente",
      "Cluster BioValley = multiples santé/pharma premium",
      "Bassin de repreneurs allemands intéressés par PME alsaciennes",
      "Tradition de transmissions familiales (Pacte Dutreil quasi-systématique)",
    ],
  },
  {
    slug: 'nice',
    name: 'Nice',
    nameLong: 'Nice (06)',
    region: "Provence-Alpes-Côte d'Azur",
    population: '340 000 habitants · 950 000 dans la métropole',
    pmeCount: '80 000+ TPE/PME',
    economy: "Capitale de la Côte d'Azur, premier pôle touristique de France après Paris. Pôle technologique Sophia Antipolis (la plus grande technopole d'Europe), santé, immobilier de prestige. Économie tertiaire dominante.",
    topSectors: [
      { name: 'Tourisme & Hôtellerie', multiple: '5-9x EBITDA', note: 'Carlton, Negresco, palaces. Multiples premium pour établissements 4-5 étoiles avec marque' },
      { name: 'Tech / Sophia Antipolis', multiple: '7-13x EBITDA', note: 'Plus grande technopole d\'Europe (Amadeus, SAP, IBM, plus de 2000 entreprises)' },
      { name: 'Immobilier de prestige', multiple: '5-10x EBITDA', note: 'Agences immobilier de luxe, gestion locative haut de gamme' },
      { name: 'Santé', multiple: '5-9x EBITDA', note: 'CHU Nice, cliniques, biotechs (Eurosciences)' },
    ],
    example: {
      title: "Exemple : Hôtel 4 étoiles niçois — 2,5M€ CA",
      desc: "Hôtel 4 étoiles 60 chambres, CA 2,5M€, EBITDA 22% (550K€) hors charges foncières, taux d'occupation 78% saison haute. Multiple appliqué : 7x EBITDA = 3,85M€. Valorisation totale 5,5M€ après ajout du fonds de commerce et de la marque locale.",
    },
    localContext: "Nice combine deux dynamiques de valorisation : le tourisme premium (multiples élevés pour hôtellerie 4-5★) et la tech (Sophia Antipolis). Les acheteurs étrangers (russes, chinois, britanniques avant Brexit) sont nombreux sur les actifs hôteliers et immobiliers. Pour la tech, les exits vers grands groupes US sont fréquents.",
    specificities: [
      "Multiples hôteliers premium (touristes haut pouvoir d'achat)",
      "Demande étrangère soutenue pour actifs prestige",
      "Sophia Antipolis = écosystème tech mature (40+ ans)",
      "Saisonnalité tourisme à intégrer dans la valorisation (EBITDA normalisé)",
    ],
  },
  {
    slug: 'rennes',
    name: 'Rennes',
    nameLong: 'Rennes (35)',
    region: 'Bretagne',
    population: '220 000 habitants · 460 000 dans la métropole',
    pmeCount: '70 000+ TPE/PME',
    economy: "Capitale bretonne, métropole jeune (75 000 étudiants). French Tech très active (Klaxoon, Niji, Sopra Steria), tissu agroalimentaire historique (porc, lait), automobile (PSA Stellantis La Janais), recherche IT (Inria, CentraleSupélec).",
    topSectors: [
      { name: 'Tech / Numérique', multiple: '7-12x EBITDA', note: 'Rennes French Tech : Klaxoon, Niji, Energiency. Bassin d\'ingénieurs (INSA, CentraleSupélec)' },
      { name: 'Agroalimentaire', multiple: '4-7x EBITDA', note: 'Pôle Valorial, coopératives (Triskalia, Cooperl). PME agro avec marques régionales' },
      { name: 'Automobile', multiple: '3-6x EBITDA', note: 'Stellantis La Janais, sous-traitance auto. Multiples plus modestes (cyclicité)' },
      { name: 'Recherche / Édition logicielle', multiple: '6-11x EBITDA', note: 'Inria Rennes, Orange Labs, Technicolor. Édition de logiciels spécialisés' },
    ],
    example: {
      title: "Exemple : SaaS rennais B2B — 600K€ ARR",
      desc: "SaaS gestion d'équipe, ARR 600K€, croissance +40%/an, 150 clients, churn 5%. Multiple appliqué : 7,5x ARR = valorisation 4,5M€. La croissance et la rétention solide justifient un multiple supérieur à 6x médiane SaaS pour des PME de cette taille.",
    },
    localContext: "Rennes combine tissu industriel traditionnel (agro, auto) et scène tech dynamique. Les transmissions familiales sont nombreuses dans l'agro coopératif. Côté tech, les exits vers grands groupes parisiens ou internationaux sont fréquents (Klaxoon, Energiency rachetée par Schneider Electric).",
    specificities: [
      "Bassin tech mature avec écosystème d'investisseurs régionaux (Breizh Up, Go Capital)",
      "Multiples agro stables (qualité bretonne reconnue)",
      "Forte attractivité talents (qualité de vie, prix immobilier modéré)",
      "Coopératives = transmissions complexes (gouvernance mutualiste)",
    ],
  },
  {
    slug: 'montpellier',
    name: 'Montpellier',
    nameLong: 'Montpellier (34)',
    region: 'Occitanie',
    population: '300 000 habitants · 500 000 dans la métropole',
    pmeCount: '70 000+ TPE/PME',
    economy: "Métropole en très forte croissance démographique (top 5 français). Tech French Tech ultra-dynamique (BIC, Teads, Predictice), santé (CHU + cluster Eurobiomed), agro vins/vignobles Languedoc, tourisme méditerranéen.",
    topSectors: [
      { name: 'Tech / SaaS', multiple: '7-13x EBITDA', note: 'Montpellier French Tech : Teads, BIC, IES Synergy. Cluster en forte croissance' },
      { name: 'Santé / Biotech', multiple: '6-10x EBITDA', note: 'Cluster Eurobiomed, BioMéditerranée, IRD. Multiples premium pour PME bio/medtech' },
      { name: 'Vins Languedoc', multiple: '4-8x EBITDA + foncier', note: 'Premier vignoble français en volume. Valorisation mixte EBITDA + foncier viticole' },
      { name: 'Agroalimentaire', multiple: '4-7x EBITDA', note: 'Marché de gros Mercat (premier de France), filière fruits/légumes' },
    ],
    example: {
      title: "Exemple : SaaS HealthTech montpelliérain — 400K€ ARR",
      desc: "SaaS télémédecine, ARR 400K€, croissance +60%/an, financé par Bpifrance + business angels. Multiple appliqué : 10x ARR = valorisation 4M€. La croissance triple-digit hyper-growth et le marché healthtech porteur justifient un multiple supérieur à 7x médiane SaaS B2B.",
    },
    localContext: "Montpellier est devenue en 5 ans l'une des métropoles tech les plus dynamiques de France. Cette croissance attire investisseurs et acquéreurs nationaux/internationaux. Pour la tech, les multiples sont au niveau de Toulouse ou Bordeaux. Les transmissions viticoles utilisent massivement le Pacte Dutreil.",
    specificities: [
      "Multiples tech premium (croissance écosystème +30% par an)",
      "Cluster santé en forte expansion (BioMéditerranée)",
      "Marché viticole en pleine restructuration (consolidation)",
      "Forte attractivité talents (qualité de vie, mer + montagne)",
    ],
  },
  {
    slug: 'grenoble',
    name: 'Grenoble',
    nameLong: 'Grenoble (38)',
    region: 'Auvergne-Rhône-Alpes',
    population: '160 000 habitants · 450 000 dans la métropole',
    pmeCount: '50 000+ TPE/PME',
    economy: "Capitale française des micro-nanotechnologies et de la recherche (CEA, CNRS, INRIA). Pôle de compétitivité mondial Minalogic. Hautement spécialisée dans la tech deep tech, l'industrie 4.0 et la santé.",
    topSectors: [
      { name: 'Microélectronique / Semi-conducteurs', multiple: '8-14x EBITDA', note: 'STMicroelectronics, Soitec, Schneider Electric R&D. PME deeptech ultra-valorisées' },
      { name: 'Tech / Deep Tech', multiple: '8-15x EBITDA', note: 'CEA Tech, plus grand pôle deeptech français. PME avec brevets = multiples très élevés' },
      { name: 'Énergie / Cleantech', multiple: '6-12x EBITDA', note: 'Schneider Electric (siège mondial), hydrogène vert, EnR' },
      { name: 'Santé / Medtech', multiple: '6-10x EBITDA', note: 'CEA Leti, biotechs, instrumentation médicale' },
    ],
    example: {
      title: "Exemple : PME deep tech grenobloise — 1M€ CA",
      desc: "Capteurs IoT industriels avec brevets propriétaires, CA 1M€, EBITDA 18% (180K€), 3 brevets actifs, 12 clients industriels grands comptes. Multiple appliqué : 9x EBITDA = valorisation 1,6M€. Les brevets et la spécificité deeptech justifient un multiple bien supérieur aux 6x médiane tech.",
    },
    localContext: "Grenoble offre les valorisations les plus élevées de France pour les PME deeptech, grâce à l'écosystème unique CEA + INRIA + écoles d'ingé (Grenoble INP, ENSE3). Les brevets sont systématiquement valorisés en plus de l'EBITDA. Les exits vers grands groupes industriels (Schneider, ST, Air Liquide) sont fréquents.",
    specificities: [
      "Multiples deep tech 30-50% au-dessus de la moyenne nationale",
      "Brevets = composante majeure de la valorisation",
      "Exits stratégiques vers grands groupes ind. (Schneider, ST, Air Liquide)",
      "Bassin d'investisseurs spécialisés deeptech (Supernova Invest, Demeter)",
    ],
  },
  {
    slug: 'reims',
    name: 'Reims',
    nameLong: 'Reims (51)',
    region: 'Grand Est',
    population: '180 000 habitants · 290 000 dans la métropole',
    pmeCount: '45 000+ TPE/PME',
    economy: "Capitale historique du Champagne (Moët, Veuve Clicquot, Pommery, Mumm). Tissu agroalimentaire dense, industrie automobile (Stellantis), logistique. Forte dimension internationale via les exports champenois.",
    topSectors: [
      { name: 'Champagne & Vins', multiple: '6-12x EBITDA + foncier', note: 'Maisons historiques. Valorisation atypique combinant DCF + foncier (1 ha champagne = 1-2M€) + stocks vieillissants' },
      { name: 'Agroalimentaire', multiple: '4-7x EBITDA', note: 'Filière betteravière (sucre Tereos), biscuiterie, fromages' },
      { name: 'Logistique', multiple: '4-7x EBITDA', note: 'Carrefour Paris-Allemagne, e-commerce, agro-logistique' },
      { name: 'Automobile', multiple: '3-6x EBITDA', note: 'Stellantis, sous-traitance auto. Multiples industrie classiques' },
    ],
    example: {
      title: "Exemple : Maison de champagne rémoise — 4M€ CA",
      desc: "Maison familiale champenoise, CA 4M€, EBITDA 18% (720K€), 60% export (UK, USA, Japon), 15 ha en propriété (AOC Champagne Premier Cru). Multiple appliqué : 8x EBITDA = 5,8M€. Valorisation totale ~22M€ après ajout des vignobles (15 M€) et stocks (1,5 M€).",
    },
    localContext: "Reims présente une particularité absolue : les valorisations champenoises mêlent EBITDA + foncier viticole + stocks vieillissants + marque. Un hectare de vignoble Champagne Grand Cru vaut entre 1,5 et 2,5 M€. Les transmissions utilisent quasi systématiquement le Pacte Dutreil et les GFV (Groupements Fonciers Viticoles).",
    specificities: [
      "Valorisation champagne = EBITDA + foncier + stocks + marque (4 piliers)",
      "Demande étrangère forte (LVMH, Pernod Ricard, chinois) pour maisons indépendantes",
      "Transmissions familiales très optimisées fiscalement",
      "Stocks vieillissants = composante valorisation importante (millésimes 5-10 ans)",
    ],
  },
  {
    slug: 'angers',
    name: 'Angers',
    nameLong: 'Angers (49)',
    region: 'Pays de la Loire',
    population: '155 000 habitants · 300 000 dans la métropole',
    pmeCount: '40 000+ TPE/PME',
    economy: "Capitale du Végétal Spécialisé (premier pôle horticole français). Tissu tech en croissance (We Network), électronique, viticole (Anjou). Métropole moyenne avec belle qualité d'entrepreneurs familiaux.",
    topSectors: [
      { name: 'Végétal / Horticulture', multiple: '4-7x EBITDA + foncier', note: 'Premier pôle horticole français (Vegepolys). PME spécialisées avec brevets variétaux' },
      { name: 'Tech / Électronique', multiple: '6-11x EBITDA', note: 'We Network (cluster IoT), Bouygues Telecom, Bull. Bassin spécialisé' },
      { name: 'Vins (Anjou)', multiple: '4-7x EBITDA + foncier', note: 'AOC Anjou, Saumur, Coteaux du Layon. Vignobles à valoriser distinctement' },
      { name: 'Agroalimentaire', multiple: '4-7x EBITDA', note: 'Cointreau, Brioche Pasquier, Giffard. Marques régionales fortes' },
    ],
    example: {
      title: "Exemple : PME horticole angevine — 1,5M€ CA",
      desc: "Production de plantes ornementales en pépinière, CA 1,5M€, EBITDA 14% (210K€), 12 ha en propriété, 80% clients GMS (Jardiland, Truffaut). Multiple appliqué : 5x EBITDA = 1,05M€. Valorisation totale 1,8M€ après ajout du foncier horticole (terres + serres).",
    },
    localContext: "Angers offre un environnement unique pour les PME végétales avec Vegepolys, le premier pôle de compétitivité européen pour la filière horticole. Pour ces entreprises, la valorisation combine EBITDA + foncier productif + brevets variétaux. Les transmissions familiales sont nombreuses dans le tissu PMI traditionnel.",
    specificities: [
      "Valorisation horticole = EBITDA + foncier + serres + brevets",
      "Cluster Vegepolys = exits stratégiques vers grands semenciers (Limagrain, Vilmorin)",
      "Multiples tech IoT en croissance (We Network)",
      "Marché de la cession très calme → opportunités pour acheteurs patients",
    ],
  },
  {
    slug: 'dijon',
    name: 'Dijon',
    nameLong: 'Dijon (21)',
    region: 'Bourgogne-Franche-Comté',
    population: '160 000 habitants · 260 000 dans la métropole',
    pmeCount: '40 000+ TPE/PME',
    economy: "Capitale des vins de Bourgogne et de la gastronomie (Cité de la Gastronomie). Industrie pharmaceutique (Urgo), agroalimentaire (Mulot & Petitjean, La Vache qui rit historique), recherche (INRA Dijon).",
    topSectors: [
      { name: 'Vins de Bourgogne', multiple: '6-12x EBITDA + foncier', note: 'Côte de Nuits, Côte de Beaune. Foncier ultra-cher (jusqu\'à 15 M€/ha en Grand Cru)' },
      { name: 'Pharmacie / Santé', multiple: '6-10x EBITDA', note: 'Urgo (siège), CHU Dijon, recherche INRA. Multiples premium' },
      { name: 'Agroalimentaire', multiple: '4-7x EBITDA', note: 'Moutarde (Maille), pain d\'épices, fromages, vins. Marques historiques' },
      { name: 'Recherche / Innovation', multiple: '5-9x EBITDA', note: 'INRA, AgroSup, recherche agricole. PME innovantes agriculture/agro' },
    ],
    example: {
      title: "Exemple : Domaine viticole bourguignon — 600K€ CA",
      desc: "Domaine 4 ha AOC Côte de Nuits, CA 600K€, EBITDA 25% (150K€), 100% mise en bouteille, 60% export. Multiple appliqué : 9x EBITDA = 1,35M€. Valorisation totale ~6M€ après ajout du foncier (4 ha en Premier Cru ≈ 4,5M€) et des stocks vieillissants.",
    },
    localContext: "Dijon est synonyme de Bourgogne viticole, où les valorisations sont les plus élevées de France. Un hectare de Grand Cru Romanée-Conti vaut plus de 15 M€. Les transmissions familiales utilisent systématiquement le Pacte Dutreil + GFV (Groupements Fonciers Viticoles) pour optimiser fiscalement.",
    specificities: [
      "Valorisation viticole bourguignonne = EBITDA + foncier (énorme) + stocks + notoriété",
      "Demande étrangère extrêmement forte (Japonais, Chinois, Américains)",
      "Foncier extrêmement rare (cessions très tendues)",
      "Industrie pharma/agro complète l'écosystème (Urgo, INRA)",
    ],
  },
];

// Helper : récupère une ville par slug
export function getCityBySlug(slug) {
  return CITIES.find(c => c.slug === slug);
}
